import { Controller } from "@hotwired/stimulus";

// Drives the "Manage Membership" modal on the admin user profile (TMS-1757), on the
// Reset 2FA modal's pattern. Choosing an action shows only that action's fields
// (the rest are disabled fieldsets, so they never submit) and states its effect.
// Confirm stays disabled until the action, its required fields and a reason are
// filled. Closing the modal puts it back as it opened.
export default class extends Controller {
  static targets = ["fields", "effect", "effectText", "reason", "confirmBtn"];
  static values = { today: String, memberType: Boolean, active: Boolean };

  connect() {
    this._boundHidden = this._reset.bind(this);
    this._boundRefresh = this.refresh.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundHidden);
    // Native inputs report "change"; the custom selects (payment method) report their own.
    this.element.addEventListener("change", this._boundRefresh);
    this.element.addEventListener("app-select:change", this._boundRefresh);
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundHidden);
    this.element.removeEventListener("change", this._boundRefresh);
    this.element.removeEventListener("app-select:change", this._boundRefresh);
  }

  onActionChange(event) {
    const value = event.detail?.value;
    this.action = typeof value === "string" ? value : "";
    this.actionEffect = event.detail?.effect || "";

    this.fieldsTargets.forEach((fieldset) => {
      const active = fieldset.dataset.membershipAction === this.action;
      fieldset.disabled = !active;
      fieldset.classList.toggle("d-none", !active);
    });

    this._showEffect(this.actionEffect);
    if (this.action === "change_end_date") this.onEndDateInput();
    this.refresh();
  }

  // A type option carries its own statement of what choosing it does.
  onOptionEffect(event) {
    if (event.detail?.effect) this._showEffect(event.detail.effect);
    this.refresh();
  }

  onEndDateInput(event) {
    const input = event?.target || this._activeFieldset()?.querySelector("input[type='date']");
    const value = input?.value;
    if (!value) {
      this._showEffect(this.actionEffect);
    } else {
      this._showEffect(`${this.actionEffect} ${this._endDateAccess(value)}`);
    }
    this.refresh();
  }

  refresh() {
    const reasonFilled = this.reasonTarget.value.trim() !== "";
    this.confirmBtnTarget.disabled = !(this.action && reasonFilled && this._requiredFieldsFilled());
  }

  _endDateAccess(value) {
    if (!this.memberTypeValue) return "Their member type isn't a membership, so the date alone doesn't give access.";
    if (value < this.todayValue) {
      return this.activeValue ? "They lose member access immediately." : "They still won't have member access.";
    }
    return this.activeValue ? "Member access continues until then." : "They gain member access immediately.";
  }

  _requiredFieldsFilled() {
    const fieldset = this._activeFieldset();
    if (!fieldset) return true;

    return Array.from(fieldset.querySelectorAll("input[required], select[required]"))
      .every((input) => input.value.trim() !== "");
  }

  _activeFieldset() {
    return this.fieldsTargets.find((fieldset) => fieldset.dataset.membershipAction === this.action);
  }

  _showEffect(text) {
    this.effectTextTarget.textContent = text || "";
    this.effectTarget.classList.toggle("d-none", !text);
  }

  // The selects are custom components that keep their value in a hidden input,
  // which a form reset leaves alone, so each is cleared through its own control.
  _reset() {
    this.element.querySelector("form")?.reset();
    this.element.querySelectorAll("[data-app-select-target='clear']").forEach((clear) => clear.click());
    this.action = "";
    this.actionEffect = "";
    this.fieldsTargets.forEach((fieldset) => {
      fieldset.disabled = true;
      fieldset.classList.add("d-none");
    });
    this._showEffect("");
    this.confirmBtnTarget.disabled = true;
  }
}
